import { List } from '@mui/icons-material';
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CircularProgress,
    Grid,
    InputBase,
    Modal,
    Stack,
} from '@mui/material';
import Typography from '@mui/material/Typography';
import {
    GoogleMap,
    MarkerF as Marker,
    useLoadScript,
} from '@react-google-maps/api';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    useGetListDataQuery,
    useGetListDataWithPostMutation,
} from '../../api/apiSlice';
import mapIcon from '../../assets/church-pin.png';
import ministerIcon from '../../assets/pastor-pin.png';
import ExternalScreen from '../../components/fragments/ExternalScreen';
import Iconify from '../../components/fragments/Iconify';
import ListEmpty from '../../components/fragments/ListEmpty';
import CardHome from '../../components/legacy/CardHome';
import useAuth from '../../hooks/useAuth';
import { rtGetListData } from '../../store/slices/listSlice';
import { googleMapsApiKey } from '../../utils/environment';

const center = { lat: -0.7400830936570708, lng: 37.19031332174936 };

const containerStyle = {
    width: `100vw`,
    height: window.innerHeight - 56 - 56 - 40,
};

export function ModalRequireAuth({ open, onCancel }) {
    const navigate = useNavigate();

    return (
        <Modal open={open}>
            <Card sx={{ mx: 2, mt: 8 }}>
                <CardContent>
                    <Typography variant="h6">
                        You need to be logged in to access this feature
                    </Typography>
                </CardContent>
                <CardActions>
                    <Button
                        onClick={() => navigate('/guest-profile')}
                        variant="contained"
                        color="primary"
                    >
                        Login / Signup
                    </Button>
                    <Button
                        onClick={onCancel}
                        variant="outlined"
                        color="primary"
                    >
                        Cancel
                    </Button>
                </CardActions>
            </Card>
        </Modal>
    );
}

function ChurchFinderTab() {
    const [modalOpen, setModalOpen] = useState(false);
    const [currentLocation, setCurrentLocation] = useState(center);
    const [resultLocation, setResultLocation] = useState(center);
    const [churchList, setChurchList] = useState([]);
    const [currentView, setCurrentView] = useState();
    const [search, setSearch] = useState('');

    const { isLoggedIn } = useAuth();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const state = useSelector((st) => st);
    const { user } = state.auth;

    const checkLocationPermission = useCallback(() => {
        navigator.permissions
            .query({ name: 'geolocation' })
            .then(({ state: st }) => {
                if (st === 'granted') {
                    getCurrentLocation();
                } else {
                    setModalOpen(true);
                }
            });
    }, []);

    const getCurrentLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    setCurrentLocation({ lat: latitude, lng: longitude });
                    setModalOpen(false);
                },
                (error) => {
                    alert(
                        `Please nable location to locate a church near you. Error: ${error.message}`
                    );
                    setModalOpen(true);
                }
            );
        } else {
            alert('Geolocation  not supported by this browser.');
        }
    };

    const { data: searchPlacesData, isLoading } = useGetListDataQuery(
        { list: 'church/finder', params: { q: search } },
        { refetchOnMountOrArgChange: true }
    );

    const [getListDataWithPost] = useGetListDataWithPostMutation();

    useEffect(() => {
        dispatch(rtGetListData('churches', 'churches'));
        checkLocationPermission();
        getListDataWithPost({
            list: 'church/finder',
            body: { long: currentLocation.lng, lat: currentLocation.lat },
        }).then(({ data }) => setChurchList(data?.data || []));
    }, [
        checkLocationPermission,
        currentLocation.lat,
        currentLocation.lng,
        dispatch,
        getListDataWithPost,
    ]);

    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey,
    });

    const searchPlaces = searchPlacesData?.data;

    return (
        <ExternalScreen
            tab
            authPass
            disablePadding
            loading={!isLoaded}
            title="Church Finder"
        >
            <Grid item xs={12}>
                {loadError && <ListEmpty title="Error Loading map" />}
                {isLoaded && (
                    <GoogleMap
                        mapContainerStyle={containerStyle}
                        center={resultLocation || currentLocation}
                        zoom={8}
                    >
                        <Marker
                            onClick={() => {
                                setResultLocation(currentLocation);
                                setCurrentView({
                                    name: `${user?.firstName} ${user?.surname}`,
                                    location: {
                                        distance: 'Your current location',
                                    },
                                });
                            }}
                            position={currentLocation}
                        />
                        {churchList?.map((church) => {
                            const churchPosition = {
                                lat: Number(church?.location?.latitude),
                                lng: Number(church?.location?.longitude),
                            };

                            return (
                                <Marker
                                    key={church.id}
                                    icon={
                                        church?.type == 'PASTOR'
                                            ? ministerIcon
                                            : mapIcon
                                    }
                                    position={churchPosition}
                                    onClick={() => {
                                        setSearch('');
                                        setCurrentView(church);
                                        setResultLocation(churchPosition);
                                    }}
                                />
                            );
                        })}
                    </GoogleMap>
                )}
            </Grid>

            {searchPlaces?.length < 1 && Boolean(currentView) && (
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    sx={{
                        p: 0.5,
                        left: 0,
                        bottom: 56 + 48 + 0,
                        width: '100%',
                        position: 'fixed',
                    }}
                >
                    <CardHome
                        full
                        title={`${currentView?.name} ${
                            currentView?.type == 'PASTOR'
                                ? '(Online Minister)'
                                : ''
                        }`}
                        desc={`${
                            parseInt(currentView?.location?.distance) || 0
                        }KM away`}
                        link={
                            currentView?.type == 'PASTOR'
                                ? `/minister/${currentView?.username}`
                                : `/church/${currentView?.id}`
                        }
                    />
                </Stack>
            )}

            {searchPlaces?.length > 0 && (
                <Stack
                    sx={{
                        p: 0.5,
                        left: 0,
                        bottom: 56 + 48 + 0,
                        width: '100%',
                        position: 'fixed',
                    }}
                >
                    {isLoading && Boolean(!currentView) && (
                        <CardHome title={<CircularProgress size={24} />} full />
                    )}
                    {searchPlaces?.map(({ address, location }) => (
                        <Grid
                            item
                            xs={12}
                            key={address}
                            onClick={() =>
                                setResultLocation({
                                    lat: Number(location?.latitude),
                                    lng: Number(location?.longitude),
                                })
                            }
                        >
                            <Card
                                sx={{ p: 1.5, mx: 0.8, my: 0.2 }}
                                variant="outlined"
                            >
                                <Typography variant="h6">{address}</Typography>
                            </Card>
                        </Grid>
                    ))}
                </Stack>
            )}

            <Stack
                direction="row"
                justifyContent="space-between"
                sx={{
                    p: 0.5,
                    left: 0,
                    bottom: 56,
                    width: '100%',
                    position: 'fixed',
                    bgcolor: 'background.default',
                }}
            >
                <InputBase
                    sx={{ ml: 1 }}
                    placeholder="Search area"
                    onChange={(e) => setSearch(e.target.value)}
                />
                <Button
                    onClick={() => navigate('/map-churches')}
                    startIcon={<List />}
                >
                    List
                </Button>
            </Stack>

            <ModalRequireAuth
                open={!isLoggedIn}
                onCancel={() => navigate(-1)}
            />

            <Modal open={modalOpen && isLoggedIn}>
                <Stack justifyContent="flex-end" sx={{ height: '100vh' }}>
                    <Box
                        sx={{
                            bgcolor: 'background.default',
                            borderTopLeftRadius: 16,
                            borderTopRightRadius: 16,
                            textAlign: 'center',
                            px: 3,
                            py: 2,
                        }}
                    >
                        <Iconify
                            icon="fluent:globe-location-24-regular"
                            sx={{
                                color: 'primary.main',
                                width: 64,
                                height: 64,
                                my: 2,
                            }}
                        />
                        <Typography color="text.primary" variant="h5">
                            Let&apos;s find you a church
                        </Typography>
                        <Typography color="text.primary" sx={{ my: 2 }}>
                            Share your location so we can help you find a church
                            and an online pastor around you
                        </Typography>
                        <Button
                            onClick={getCurrentLocation}
                            sx={{ mb: 1 }}
                            fullWidth
                            variant="contained"
                        >
                            Share Location
                        </Button>
                        <Button
                            onClick={() => setModalOpen(false)}
                            fullWidth
                            variant="outlined"
                        >
                            Share Later
                        </Button>
                    </Box>
                </Stack>
            </Modal>
        </ExternalScreen>
    );
}

// export default React.memo(ChurchFinderTab);
export default ChurchFinderTab;
